/**
 * ZIP Code Boundaries Service
 *
 * Service for fetching ZIP code boundary geometries and unioning them into a single polygon
 */

import * as turf from '@turf/turf';
import type { Feature, Polygon, MultiPolygon } from 'geojson';
import type { NormalizedGeometry } from '../utils/zoneGeometry';

export interface ZipCodeBoundaryResponse {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    properties: {
      ZCTA5CE10: string; // ZIP code
      [key: string]: unknown;
    };
    geometry: Polygon | MultiPolygon;
  }>;
}

/**
 * Fetch ZIP code boundaries from Census TIGERweb API
 * Uses the existing get-zips Edge Function, but requests geometries instead of just ZIP codes.
 *
 * Exported so callers that want the **individual** ZIP boundaries (not the
 * unioned zone outline produced by `createZoneBoundaryFromZipCodes`) can
 * consume the raw FeatureCollection — e.g. the Presentation-mode renderer
 * that draws ZIP subdivisions inside each zone.
 */
export async function fetchZipCodeBoundaries(
  zipCodes: string[]
): Promise<Feature<Polygon | MultiPolygon>[]> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const projectRef = supabaseUrl?.match(/https?:\/\/([^.]+)\.supabase\.co/)?.[1];

  if (!projectRef) {
    throw new Error('Invalid Supabase URL configuration');
  }

  // Get session for authenticated request
  const { supabase } = await import('@vehix/shared/admin/portal-bridge');
  const {
    data: { session }
  } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('Authentication required');
  }

  // Call Edge Function to fetch ZIP boundaries
  // We'll modify get-zips to support returning geometries when zip_codes are provided
  const edgeFunctionUrl = `${supabaseUrl}/functions/v1/get-zips`;

  const response = await fetch(edgeFunctionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY || ''
    },
    body: JSON.stringify({ zip_codes: zipCodes, return_geometry: true })
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to fetch ZIP boundaries: ${response.status} ${response.statusText} - ${errorText}`
    );
  }

  const data: ZipCodeBoundaryResponse = await response.json();

  // Convert to Turf Feature array
  const features = data.features.map(feature => {
    // Log first feature's properties to debug field names
    if (data.features.indexOf(feature) === 0 && feature.properties) {
    }

    return {
      type: 'Feature' as const,
      properties: feature.properties,
      geometry: feature.geometry
    };
  });

  return features;
}

/**
 * Union multiple ZIP code boundaries into a single polygon
 * Uses Turf.js to merge all ZIP code geometries
 */
export async function createZoneBoundaryFromZipCodes(
  zipCodes: string[]
): Promise<NormalizedGeometry | null> {
  if (!zipCodes || zipCodes.length === 0) {
    return null;
  }

  try {
    // Fetch boundaries for all ZIP codes
    const boundaries = await fetchZipCodeBoundaries(zipCodes);

    if (boundaries.length === 0) {
      return null;
    }

    if (boundaries.length < zipCodes.length) {
      const foundZipCodes = boundaries
        .map(b => b.properties?.ZCTA5 || b.properties?.ZCTA5CE10)
        .filter(Boolean);
      const missingZipCodes = zipCodes.filter(zip => !foundZipCodes.includes(zip));
    }

    // Remove any duplicate boundaries (same ZIP might be returned multiple times)
    const uniqueBoundaries = boundaries.reduce((acc: typeof boundaries, boundary) => {
      // Try different property names that Census API might use
      const zipCode =
        boundary.properties?.ZCTA5 ||
        boundary.properties?.ZCTA5CE10 ||
        boundary.properties?.GEOID ||
        boundary.properties?.ZCTA;

      const exists = acc.some(b => {
        const existingZip =
          b.properties?.ZCTA5 ||
          b.properties?.ZCTA5CE10 ||
          b.properties?.GEOID ||
          b.properties?.ZCTA;
        return existingZip === zipCode;
      });

      if (!exists && zipCode) {
        acc.push(boundary);
      }
      return acc;
    }, []);

    // Union all boundaries into a single feature
    // If only one boundary, return it directly
    if (uniqueBoundaries.length === 1) {
      const singleBoundary = uniqueBoundaries[0];
      const geometry = singleBoundary.geometry;

      if (geometry.type === 'Polygon') {
        return {
          type: 'Polygon',
          coordinates: geometry.coordinates as number[][][]
        };
      } else if (geometry.type === 'MultiPolygon') {
        return {
          type: 'MultiPolygon',
          coordinates: geometry.coordinates as number[][][][]
        };
      }
      return null;
    }

    // For multiple boundaries, union them
    // Use turf.unionAll if available, otherwise union sequentially
    // Try using combine first to merge all features, then union

    if (uniqueBoundaries.length === 1) {
      // Single boundary - return it directly
      const geometry = uniqueBoundaries[0].geometry;
      if (geometry.type === 'Polygon') {
        return {
          type: 'Polygon',
          coordinates: geometry.coordinates as number[][][]
        };
      } else if (geometry.type === 'MultiPolygon') {
        return {
          type: 'MultiPolygon',
          coordinates: geometry.coordinates as number[][][][]
        };
      }
      return null;
    }

    // For multiple boundaries, try different union strategies

    // Strategy 1: Try to create a MultiPolygon from all boundaries if union fails
    const allPolygons: Array<Polygon | MultiPolygon> = [];

    // Collect all polygons
    for (const boundary of uniqueBoundaries) {
      if (boundary.geometry) {
        if (boundary.geometry.type === 'Polygon') {
          allPolygons.push(boundary.geometry);
        } else if (boundary.geometry.type === 'MultiPolygon') {
          // Flatten MultiPolygon into individual Polygons
          for (const coords of boundary.geometry.coordinates) {
            allPolygons.push({
              type: 'Polygon',
              coordinates: coords
            });
          }
        }
      }
    }

    // Try different strategies
    let result: NormalizedGeometry | null = null;

    // Strategy 1: Create MultiPolygon from all ZIP code boundaries
    try {
      // Collect all valid polygon coordinates
      const multiPolygonCoordinates: number[][][][] = [];
      let processedZipCodes = 0;

      for (const boundary of uniqueBoundaries) {
        try {
          const geometry = boundary.geometry;
          const zipCode =
            boundary.properties?.ZCTA5 ||
            boundary.properties?.ZCTA5CE10 ||
            `boundary_${processedZipCodes}`;

          if (geometry.type === 'Polygon') {
            multiPolygonCoordinates.push(geometry.coordinates);
            processedZipCodes++;
          } else if (geometry.type === 'MultiPolygon') {
            // Add each polygon from the MultiPolygon
            geometry.coordinates.forEach((polygonCoords: number[][][]) => {
              multiPolygonCoordinates.push(polygonCoords);
            });
            processedZipCodes++;
          }
        } catch (error) {
          const zipCode = boundary.properties?.ZCTA5 || boundary.properties?.ZCTA5CE10 || 'unknown';
        }
      }

      if (multiPolygonCoordinates.length > 0) {
        result = {
          type: 'MultiPolygon',
          coordinates: multiPolygonCoordinates
        };
      }
    } catch (error) {}

    // Strategy 2: Create simplified boundary as fallback only if MultiPolygon failed
    const shouldUseFallback = !result;

    if (shouldUseFallback && allPolygons.length > 0) {
      try {
        // Create a FeatureCollection from all polygons
        const featureCollection: any = {
          type: 'FeatureCollection',
          features: allPolygons.map(polygon => ({
            type: 'Feature',
            geometry: polygon,
            properties: {}
          }))
        };

        // Try different approaches for a clean unified boundary
        let cleanResult: any = null;

        // Approach 1: Try buffering and unioning all polygons together
        try {
          // Create a union of all features using a more robust approach
          let combinedFeature = featureCollection.features[0];

          for (let i = 1; i < featureCollection.features.length && combinedFeature; i++) {
            try {
              // Add a tiny buffer to help with union operations
              const buffered1 = turf.buffer(combinedFeature, 0.0001, { units: 'kilometers' });
              const buffered2 = turf.buffer(featureCollection.features[i], 0.0001, {
                units: 'kilometers'
              });

              const unionAttempt = turf.union(buffered1, buffered2);
              if (unionAttempt?.geometry) {
                combinedFeature = unionAttempt;
              }
            } catch (unionError) {
              // Continue with what we have
            }
          }

          if (combinedFeature?.geometry) {
            // Remove the tiny buffer to get back to original size
            try {
              const unbuffered = turf.buffer(combinedFeature, -0.0001, { units: 'kilometers' });
              cleanResult = unbuffered?.geometry ? unbuffered : combinedFeature;
            } catch {
              cleanResult = combinedFeature;
            }
          }
        } catch (bufferError) {}

        // Approach 2: If buffering failed, try convex hull as last resort
        if (!cleanResult) {
          try {
            // Get all coordinates from all polygons
            const allCoords: number[][] = [];
            featureCollection.features.forEach((feature: any) => {
              if (feature.geometry.type === 'Polygon') {
                // Add coordinates from outer ring only
                feature.geometry.coordinates[0].forEach((coord: number[]) => {
                  allCoords.push(coord);
                });
              }
            });

            if (allCoords.length > 0) {
              const points = turf.featureCollection(allCoords.map(coord => turf.point(coord)));
              const hull = turf.convex(points);
              if (hull?.geometry) {
                cleanResult = hull;
              }
            }
          } catch (hullError) {}
        }

        // Use the clean result if we got one
        if (cleanResult?.geometry) {
          result = cleanResult.geometry as NormalizedGeometry;
        } else {
          // Last resort: Use the best union result we have, even if incomplete
        }
      } catch (error) {}
    } else if (result) {
    }

    // Keep MultiPolygon as-is - it preserves the exact ZIP code boundaries
    if (result && result.type === 'MultiPolygon') {
    }

    return result;
  } catch (error) {
    // Return null on error - zone will keep original drawn geometry
    return null;
  }
}
